import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';

// ── Crypto coins fetched from CoinGecko ──
const COINGECKO_IDS = [
  { id: 'bitcoin', symbol: 'BTC' },
  { id: 'ethereum', symbol: 'ETH' },
  { id: 'solana', symbol: 'SOL' },
  { id: 'ripple', symbol: 'XRP' },
  { id: 'dogecoin', symbol: 'DOGE' },
  { id: 'cardano', symbol: 'ADA' },
  { id: 'avalanche-2', symbol: 'AVAX' },
  { id: 'chainlink', symbol: 'LINK' },
  { id: 'polkadot', symbol: 'DOT' },
  { id: 'litecoin', symbol: 'LTC' },
];

// ── Stocks & Forex (static — no free real-time API without key) ──
const STOCK_ITEMS = [
  { symbol: 'TSLA', price: 172.90, change: -1.76 },
  { symbol: 'MSFT', price: 415.30, change: 0.67 },
  { symbol: 'NVDA', price: 875.20, change: 1.44 },
  { symbol: 'AAPL', price: 213.45, change: 0.86 },
  { symbol: 'AMZN', price: 198.60, change: 0.58 },
  { symbol: 'GOOGL', price: 175.80, change: -0.54 },
  { symbol: 'META', price: 542.10, change: 1.55 },
  { symbol: 'SPY', price: 518.40, change: 0.24 },
  { symbol: 'QQQ', price: 444.20, change: 0.70 },
];

const FOREX_ITEMS = [
  { symbol: 'EUR/USD', price: 1.0842, change: 0.11 },
  { symbol: 'GBP/USD', price: 1.2715, change: -0.06 },
  { symbol: 'USD/JPY', price: 151.42, change: 0.18 },
  { symbol: 'GOLD', price: 2345.80, change: 0.53 },
  { symbol: 'OIL', price: 82.45, change: -1.02 },
];

const STATIC_CRYPTO_FALLBACK = [
  { symbol: 'BTC', price: 67842.0, change: 1.26 },
  { symbol: 'ETH', price: 3412.5, change: -1.31 },
  { symbol: 'SOL', price: 182.3, change: 2.71 },
  { symbol: 'XRP', price: 0.6284, change: 0.58 },
  { symbol: 'DOGE', price: 0.1642, change: -0.76 },
  { symbol: 'ADA', price: 0.4821, change: 1.14 },
  { symbol: 'AVAX', price: 38.45, change: -2.12 },
  { symbol: 'LINK', price: 18.92, change: 3.45 },
  { symbol: 'DOT', price: 7.84, change: -0.54 },
  { symbol: 'LTC', price: 84.2, change: 0.67 },
];

function formatPrice(price, symbol) {
  const isForex = symbol && symbol.includes('/');
  if (isForex && price < 10) return price.toFixed(4);
  if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (price >= 1) return price.toFixed(2);
  if (price >= 0.01) return price.toFixed(4);
  return price.toFixed(6);
}

export default function CryptoTicker() {
  const [cryptoItems, setCryptoItems] = useState(STATIC_CRYPTO_FALLBACK);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchPrices() {
      try {
        const ids = COINGECKO_IDS.map(c => c.id).join(',');
        const res = await fetch(
          `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`,
          { signal: AbortSignal.timeout(5000) }
        );
        if (!res.ok) throw new Error('API error');
        const data = await res.json();
        if (cancelled) return;

        const mapped = COINGECKO_IDS.map(coin => {
          const d = data[coin.id];
          if (!d) return null;
          return {
            symbol: coin.symbol,
            price: d.usd || 0,
            change: d.usd_24h_change || 0,
          };
        }).filter(Boolean);

        if (mapped.length > 0) setCryptoItems(mapped);
      } catch {
        // Keep static fallback
      }
    }

    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Interleave: stocks → crypto → forex for variety, then duplicate for seamless loop
  const allItems = [...STOCK_ITEMS.slice(0, 4), ...cryptoItems.slice(0, 5), ...FOREX_ITEMS.slice(0, 3), ...STOCK_ITEMS.slice(4), ...cryptoItems.slice(5), ...FOREX_ITEMS.slice(3)];
  const doubled = [...allItems, ...allItems];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 56,
        left: 0,
        right: 0,
        zIndex: 999,
        overflow: 'hidden',
        bgcolor: 'rgba(5,8,15,0.92)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        py: 0.8,
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          display: 'inline-flex',
          whiteSpace: 'nowrap',
          animation: 'tickerScroll 50s linear infinite',
          '&:hover': { animationPlayState: 'paused' },
          '@keyframes tickerScroll': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(-50%)' },
          },
        }}
      >
        {doubled.map((item, i) => {
          const isUp = item.change >= 0;
          return (
            <Box
              key={`${item.symbol}-${i}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.8,
                px: 2,
                borderRight: '1px solid rgba(255,255,255,0.06)',
                cursor: 'default',
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.7 },
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  color: '#F9FAFB',
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  letterSpacing: '0.02em',
                }}
              >
                {item.symbol}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  color: '#9CA3AF',
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                }}
              >
                {item.symbol.includes('/') ? '' : '$'}{formatPrice(item.price, item.symbol)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  color: isUp ? '#00FF94' : '#EF4444',
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                }}
              >
                {isUp ? '▲' : '▼'} {isUp ? '+' : ''}{item.change.toFixed(2)}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
